import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { sendOtp, verifyOtp } from '../../services/patientService';
import { isValidPhone } from '../../utils/helpers';
import AppLogo from '../../components/common/AppLogo';

export default function PatientLoginPage() {

    const smartErr = (msg) => {
      const m = msg || 'Something went wrong';
      if (m.toLowerCase().includes('timeout') || m.toLowerCase().includes('taking too long') || m.toLowerCase().includes('cannot reach'))
        return 'Server is busy or unreachable. Please check your internet and try again.';
      return m;
    };
  const navigate              = useNavigate();
  const location              = useLocation();
  const { loginSuccess }      = useAuth();
  const [step, setStep]       = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone]     = useState('');
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer]     = useState(0);

  // Start 60s resend timer
  const startTimer = () => {
    setTimer(60);
    const t = setInterval(() => {
      setTimer((prev) => { if (prev <= 1) { clearInterval(t); return 0; } return prev - 1; });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (!isValidPhone(phone)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      await sendOtp(phone);
      toast.success('OTP sent!');
      setStep('otp');
      startTimer();
    } catch (err) {
      toast.error(smartErr(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const res = await verifyOtp(phone, otp);
      loginSuccess(res.token, res.user, 'patient');
      toast.success('Welcome to Faristaa!');

      // If new user — go to profile setup first
      if (res.isNewUser || !res.user?.isProfileComplete) {
        navigate('/setup-profile', { replace: true });
        return;
      }

      // After login: go back to exactly where the patient came from
      // Priority 1: pending booking (doctor selected slot, hit login wall)
      const pending = sessionStorage.getItem('pendingBooking');
      if (pending) {
        try {
          const { doctorId, slotId, type } = JSON.parse(pending);
          sessionStorage.removeItem('pendingBooking');
          if (doctorId && slotId) {
            // Go directly to booking page — BookingPage will re-fetch slot from slotId
            navigate(`/book/${doctorId}`, { replace: true, state: { slotId, type } });
            return;
          }
          if (doctorId) { navigate(`/doctor/${doctorId}`, { replace: true }); return; }
        } catch { sessionStorage.removeItem('pendingBooking'); }
      }
      // Priority 2: 'from' in router state (set by PatientRoute redirect)
      const from = location.state?.from;
      if (from && from !== '/login') { navigate(from, { replace: true }); return; }
      // Priority 3: saved origin page (set when patient manually clicked Login)
      const savedOrigin = sessionStorage.getItem('loginOrigin');
      if (savedOrigin) {
        sessionStorage.removeItem('loginOrigin');
        if (savedOrigin !== '/login') { navigate(savedOrigin, { replace: true }); return; }
      }
      navigate('/');
    } catch (err) {
      toast.error(smartErr(err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* Top logo bar */}
      <div className="flex items-center px-4 py-4 border-b border-border bg-white">
        <button onClick={() => navigate(-1)} className="btn-icon mr-2">
          <i className="fa-solid fa-arrow-left text-text-primary" />
        </button>
        <AppLogo size="sm" />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 py-8 max-w-md mx-auto w-full">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <AppLogo size="md" />
        </div>

        <h1 className="text-2xl font-bold text-text-primary font-heading mb-1 text-center">
          {step === 'phone' ? 'Login / Sign up' : 'Verify OTP'}
        </h1>
        <p className="text-sm text-text-secondary text-center mb-8">
          {step === 'phone'
            ? 'Enter your mobile number to continue'
            : `OTP sent to +91 ${phone}`}
        </p>

        {step === 'phone' ? (
          <div className="space-y-4">
            <div>
              <label className="input-label">Mobile Number</label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 border border-border rounded-btn bg-gray-50 text-sm text-text-secondary font-medium">
                  +91
                </div>
                <input
                  type="tel"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="10-digit number"
                  className="input flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                  autoFocus
                />
              </div>
            </div>
            <button
              onClick={handleSendOtp}
              disabled={loading || phone.length !== 10}
              className="btn-primary"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fa-solid fa-spinner animate-spin" /> Sending OTP...
                </span>
              ) : 'Get OTP'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* OTP boxes */}
            <div>
              <label className="input-label">Enter 6-digit OTP</label>
              <input
                type="tel"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="• • • • • •"
                className="input text-center text-2xl tracking-[0.5em] font-bold"
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                autoFocus
              />
            </div>

            <button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length !== 6}
              className="btn-primary"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fa-solid fa-spinner animate-spin" /> Verifying...
                </span>
              ) : 'Verify & Login'}
            </button>

            {/* Resend */}
            <div className="text-center">
              {timer > 0 ? (
                <p className="text-sm text-text-muted">Resend OTP in <span className="text-primary font-semibold">{timer}s</span></p>
              ) : (
                <button
                  onClick={() => { setOtp(''); handleSendOtp(); }}
                  className="text-sm text-primary font-semibold"
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button
              onClick={() => { setStep('phone'); setOtp(''); }}
              className="w-full text-sm text-text-secondary text-center"
            >
              ← Change number
            </button>
          </div>
        )}

        <p className="text-xs text-text-muted text-center mt-8 px-4">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
